/**
 * Realtime Hook
 *
 * Connects to the broadcast worker for real-time updates.
 * Follows Supabase Realtime format conventions.
 *
 * @example
 * ```tsx
 * const { subscribe, isConnected } = useRealtime();
 *
 * useEffect(() => {
 *   const unsubscribe = subscribe('postgres_changes', {
 *     table: 'claims',
 *     event: '*',
 *   }, (payload) => {
 *     console.log('Claim changed:', payload);
 *     queryClient.invalidateQueries(['claims']);
 *   });
 *
 *   return unsubscribe;
 * }, [subscribe]);
 * ```
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useActiveOrganization, useSession } from '../auth/authClient';

// Environment config
const REALTIME_URL = import.meta.env.VITE_REALTIME_URL || 'wss://news-assist-api-broadcast.kardoe.workers.dev';

// Supabase-compatible types
export type PostgresChangeEvent = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

export interface PostgresChangesPayload<T = Record<string, unknown>> {
  schema: string;
  table: string;
  commit_timestamp: string;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: T | null;
  old: Partial<T> | null;
  errors: string[] | null;
}

export interface PostgresChangesFilter {
  table?: string;
  schema?: string;
  event?: PostgresChangeEvent;
  filter?: string;
}

export interface BroadcastPayload {
  event: string;
  payload: Record<string, unknown>;
}

type MessageHandler<T = unknown> = (payload: T) => void;

interface Subscription {
  type: 'postgres_changes' | 'broadcast';
  filter?: PostgresChangesFilter;
  event?: string;
  handler: MessageHandler<any>;
}

export function useRealtime() {
  const { data: session } = useSession();
  const { data: activeOrg } = useActiveOrganization();
  const wsRef = useRef<WebSocket | null>(null);
  const subscriptionsRef = useRef<Subscription[]>([]);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(() => {
    if (!session?.session?.token || !activeOrg?.id) {
      return;
    }

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      const ws = new WebSocket(`${REALTIME_URL}/realtime/v1/websocket`);
      wsRef.current = ws;

      ws.onopen = () => {
        // Send auth message
        ws.send(JSON.stringify({
          type: 'auth',
          token: localStorage.getItem('bearer_token') || session.session.token,
          organizationId: activeOrg.id,
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle auth response
          if (data.type === 'auth_success') {
            setIsConnected(true);
            setError(null);
            console.log('[Realtime] Connected to', activeOrg.id);
            return;
          }

          // Handle postgres_changes
          if (data.type === 'postgres_changes') {
            const payload = data.payload as PostgresChangesPayload;
            subscriptionsRef.current.forEach((sub) => {
              if (sub.type !== 'postgres_changes') return;

              const filter = sub.filter || {};
              const matchesTable = !filter.table || filter.table === payload.table;
              const matchesSchema = !filter.schema || filter.schema === payload.schema;
              const matchesEvent = !filter.event || filter.event === '*' || filter.event === payload.eventType;

              if (matchesTable && matchesSchema && matchesEvent) {
                sub.handler(payload);
              }
            });
            return;
          }

          // Handle broadcast
          if (data.type === 'broadcast') {
            subscriptionsRef.current.forEach((sub) => {
              if (sub.type !== 'broadcast') return;
              if (!sub.event || sub.event === '*' || sub.event === data.event) {
                sub.handler(data.payload);
              }
            });
            return;
          }
        } catch (e) {
          console.error('[Realtime] Failed to parse message:', e);
        }
      };

      ws.onerror = (e) => {
        console.error('[Realtime] WebSocket error:', e);
        setError('Connection error');
      };

      ws.onclose = (e) => {
        setIsConnected(false);
        console.log('[Realtime] Disconnected:', e.code, e.reason);

        // Reconnect after 3 seconds (unless intentionally closed)
        if (e.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[Realtime] Reconnecting...');
            connect();
          }, 3000);
        }
      };
    } catch (e) {
      console.error('[Realtime] Failed to connect:', e);
      setError('Failed to connect');
    }
  }, [session?.session?.token, activeOrg?.id]);

  // Connect when session and org are available
  useEffect(() => {
    if (session?.session?.token && activeOrg?.id) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
      }
    };
  }, [connect, session?.session?.token, activeOrg?.id]);

  /**
   * Subscribe to postgres_changes (database events)
   */
  const subscribeToChanges = useCallback(<T = Record<string, unknown>>(
    filter: PostgresChangesFilter,
    handler: MessageHandler<PostgresChangesPayload<T>>
  ): (() => void) => {
    const subscription: Subscription = {
      type: 'postgres_changes',
      filter,
      handler,
    };

    subscriptionsRef.current.push(subscription);

    return () => {
      subscriptionsRef.current = subscriptionsRef.current.filter((s) => s !== subscription);
    };
  }, []);

  /**
   * Subscribe to broadcast messages (custom events)
   */
  const subscribeToBroadcast = useCallback((
    event: string,
    handler: MessageHandler<Record<string, unknown>>
  ): (() => void) => {
    const subscription: Subscription = {
      type: 'broadcast',
      event,
      handler,
    };

    subscriptionsRef.current.push(subscription);

    return () => {
      subscriptionsRef.current = subscriptionsRef.current.filter((s) => s !== subscription);
    };
  }, []);

  /**
   * Generic subscribe (Supabase-like API)
   */
  const subscribe = useCallback(<T = unknown>(
    type: 'postgres_changes' | 'broadcast',
    options: PostgresChangesFilter | { event: string },
    handler: MessageHandler<T>
  ): (() => void) => {
    if (type === 'postgres_changes') {
      return subscribeToChanges(options as PostgresChangesFilter, handler as MessageHandler<PostgresChangesPayload>);
    } else {
      return subscribeToBroadcast((options as { event: string }).event, handler as MessageHandler<Record<string, unknown>>);
    }
  }, [subscribeToChanges, subscribeToBroadcast]);

  /**
   * Manually disconnect
   */
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
    }
  }, []);

  return {
    isConnected,
    error,
    subscribe,
    subscribeToChanges,
    subscribeToBroadcast,
    connect,
    disconnect,
  };
}

/**
 * Hook to subscribe to a specific table's changes
 */
export function useTableChanges<T = Record<string, unknown>>(
  table: string,
  event: PostgresChangeEvent = '*',
  onUpdate?: (payload: PostgresChangesPayload<T>) => void
) {
  const { subscribeToChanges, isConnected } = useRealtime();
  const [lastChange, setLastChange] = useState<PostgresChangesPayload<T> | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToChanges<T>(
      { table, event },
      (payload) => {
        setLastChange(payload);
        onUpdate?.(payload);
      }
    );

    return unsubscribe;
  }, [table, event, subscribeToChanges, onUpdate]);

  return { lastChange, isConnected };
}

export default useRealtime;
